import { DecimalPipe } from "@angular/common";
import { Component, computed, effect, inject, input, signal, untracked, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree } from "@angular/forms/signals";
import { SbAsins, SbCreativeType, StorePage } from "@front/m19-api-client";
import { MAX_ASINS_PER_STRATEGY } from "@front/m19-injection-token";
import { listToSbAsins, sbAsinsToList } from "@front/m19-models";
import { AccountSelectionService, AsinService, AuthService, BrandAssetsService, Constant } from "@front/m19-services";
import {
  IButtonDirective,
  IIconComponent,
  ITabComponent,
  ITabsSelectorComponent,
  ITooltipDirective,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { FormPreviewSplitComponent } from "@m19-board/shared/form-preview-split/form-preview-split.component";
import { CreativePreviewListComponent } from "@m19-board/strategies/sb-create-strategy/creative-preview-list/creative-preview-list.component";
import { SbFormAssetsModel } from "@m19-board/strategies/sb-create-strategy/steps/sb-form-assets/sb-form-assets.component";
import {
  SbAsinGroup,
  StoreSpotlightPagesComponent,
} from "@m19-board/strategies/sb-create-strategy/store-spotlight-pages/store-spotlight-pages.component";
import { adLineSharesSameParent, sbAsinsErrorForGroup } from "@m19-board/utils/validators/sbAsinsValidators";
import { map, startWith, switchMap } from "rxjs";

export interface SbCreativesSbAsinsModel {
  sbAsins: SbAsins[];
}

type SbCreativeModel = {
  id: number;
  label: string;
  asins: string[];
  invalid: boolean;
};

interface AsinBounds {
  min: number;
  max: number;
}

@Component({
  selector: "app-sb-creative-builder",
  imports: [
    ITabsSelectorComponent,
    ITabComponent,
    AsinSelectionComponent,
    CreativePreviewListComponent,
    FormPreviewSplitComponent,
    StoreSpotlightPagesComponent,
    IIconComponent,
    IButtonDirective,
    ITooltipDirective,
    TranslocoDirective,
    DecimalPipe,
  ],
  host: {
    class: "flex h-full min-h-0 flex-col",
  },
  templateUrl: "./sb-creative-builder.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SbCreativeBuilderComponent {
  private readonly maxAsinsPerStrategy = inject<number>(MAX_ASINS_PER_STRATEGY);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly asinService = inject(AsinService);
  private readonly brandAssetsService = inject(BrandAssetsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });

  readonly creativesSbAsins = input.required<FieldTree<SbCreativesSbAsinsModel>>();
  readonly assets = input.required<FieldTree<SbFormAssetsModel>>();
  readonly creativeType = input<SbCreativeType>(SbCreativeType.productCollection);
  readonly store = input<StorePage | null>(null);
  readonly tacosSpAsins = input<string[] | undefined>(undefined);

  selectedCreativeId = signal<number>(0);

  private readonly catalog = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
    ),
  );

  readonly marketplace = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(map((am) => am.marketplace)),
    { requireSync: true },
  );

  private readonly allStorePages = toSignal(this.brandAssetsService.storePages$.pipe(startWith([])), {
    requireSync: true,
  });

  readonly isVideoCreative = computed(() => this.creativeType() === SbCreativeType.video);
  readonly isManualCollection = computed(() => this.creativeType() === SbCreativeType.manualCollection);
  readonly isStoreSpotlight = computed(() => this.creativeType() === SbCreativeType.storeSpotlight);
  readonly storeAsinList = computed(() => {
    const store = this.store();
    if (!store) return undefined;
    return store.asinList ?? [];
  });
  readonly asinCustomList = computed(() => this.tacosSpAsins() ?? this.storeAsinList());

  readonly spotlightStorePages = computed(() => {
    const store = this.store();
    if (!store) return [];
    const storeId = store.storePageId;
    return this.allStorePages().filter((page) => page.storeId === storeId || page.storePageId === storeId);
  });

  readonly existingSpotlightSbAsins = signal<SbAsinGroup | undefined>(undefined);

  readonly creatives = computed(() => {
    const groups = this.creativesSbAsins().sbAsins().value();
    const creativeType = this.creativeType();
    return toSbCreativeModel(groups, (n) =>
      this.translocoService.translate("creative-preview-list.creative_n", { n }),
    ).map((creative) => ({
      ...creative,
      invalid: sbAsinsErrorForGroup(groups, creative.id, creativeType) !== null,
    }));
  });
  readonly selectedCreative = computed(() => this.creatives()[this.selectedCreativeId()]);
  readonly selectedAsins = computed(() => {
    if (this.isVideoCreative()) {
      return this.creativesSbAsins()
        .sbAsins()
        .value()
        .map((group) => group.asin1)
        .filter((asin): asin is string => !!asin);
    }
    return this.selectedCreative().asins;
  });
  readonly asinsLimit = computed<AsinBounds>(() => {
    switch (this.creativeType()) {
      case SbCreativeType.manualCollection:
      case SbCreativeType.productCollection:
        return { min: Constant.minAsinPerManualCollectionAdLine, max: Constant.maxAsinPerManualCollectionAdLine };
      case SbCreativeType.video:
        return { min: 1, max: Constant.maxAsinPerVideoAdLine };
      case SbCreativeType.brandVideo:
        return { min: 1, max: Constant.maxAsinPerBrandVideoAdLine };
      default:
        return { min: 1, max: this.maxAsinsPerStrategy };
    }
  });

  readonly canAddCreative = computed(() => {
    if (this.isVideoCreative() || this.isStoreSpotlight()) return false;
    const creatives = this.creatives();
    const last = creatives[creatives.length - 1];
    return last !== undefined && last.asins.length > 0;
  });

  readonly selectedCreativeErrorMessage = computed(() => {
    if (this.isStoreSpotlight()) return null;
    const error = sbAsinsErrorForGroup(
      this.creativesSbAsins().sbAsins().value(),
      this.selectedCreativeId(),
      this.creativeType(),
    );
    const message = error?.message ?? null;
    return message === "sb-form-asins.select_asins_for_your_creative" ? null : message;
  });

  readonly showSameParentWarning = computed(() => {
    const creativeType = this.creativeType();
    if (
      creativeType !== SbCreativeType.manualCollection &&
      creativeType !== SbCreativeType.productCollection &&
      creativeType !== SbCreativeType.brandVideo
    ) {
      return false;
    }
    const catalog = this.catalog();
    const groups = this.creativesSbAsins().sbAsins().value();
    const group = groups[this.selectedCreativeId()];
    if (!catalog || !group) return false;
    return adLineSharesSameParent(group, catalog);
  });

  constructor() {
    effect(() => {
      const isSpotlight = this.isStoreSpotlight();
      const storeId = this.store()?.storePageId;
      if (!isSpotlight || !storeId) {
        this.existingSpotlightSbAsins.set(undefined);
        return;
      }

      const groups = untracked(() => this.creativesSbAsins().sbAsins().value());
      const group = groups[0];
      this.existingSpotlightSbAsins.set(group && sbAsinsToList(group).length > 0 ? { asins: groups } : undefined);
    });
  }

  onSelectedAsinsChange(asins: string[]) {
    if (this.isVideoCreative()) {
      const next = asins.map((asin) => ({ asin1: asin }));
      this.creativesSbAsins().sbAsins().value.set(next);
      if (this.selectedCreativeId() >= next.length) {
        this.selectedCreativeId.set(Math.max(0, next.length - 1));
      }
      return;
    }

    const index = this.selectedCreativeId();
    const next = [...this.creativesSbAsins().sbAsins().value()];
    next[index] = listToSbAsins(asins);
    this.creativesSbAsins().sbAsins().value.set(next);
  }

  onSpotlightSbAsinsChange(asins: SbAsins[]) {
    this.creativesSbAsins().sbAsins().value.set(asins);
  }

  addCreative() {
    if (this.isVideoCreative() || this.isStoreSpotlight()) return;
    const current = this.creativesSbAsins().sbAsins().value();
    const next = [...current, emptySbAsins()];
    this.creativesSbAsins().sbAsins().value.set(next);
    this.selectedCreativeId.set(next.length - 1);
  }

  deleteCreative() {
    const current = this.creativesSbAsins().sbAsins().value();
    const next = current.filter((_, index) => index !== this.selectedCreativeId());
    this.creativesSbAsins().sbAsins().value.set(next);
    this.selectedCreativeId.set(Math.max(0, next.length - 1));
  }
}

function emptySbAsins(): SbAsins {
  return listToSbAsins([]);
}

function toSbCreativeModel(
  sbAsins: SbAsins[],
  creativeLabel: (n: number) => string,
): Omit<SbCreativeModel, "invalid">[] {
  if (sbAsins.length === 0)
    return [
      {
        id: 0,
        label: creativeLabel(1),
        asins: [],
      },
    ];
  return sbAsins.map((asin, index) => ({
    id: index,
    label: creativeLabel(index + 1),
    asins: sbAsinsToList(asin),
  }));
}
